import { useEffect, useState } from "react";

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

import { clsx } from "clsx";
import { FaRegStar, FaStar, FaStarHalfAlt } from "react-icons/fa";

type ImageProps = {
  src: string;
  alt?: string;
};

type Testimonial = {
  logo: ImageProps;
  quote: string;
  avatar: ImageProps;
  name: string;
  position: string;
  data: string;
  stars: number;
};

type Props = {
  heading: string;
  description: string;
  testimonials: Testimonial[];
};

export type TestimonialSectionProps =
  React.ComponentPropsWithoutRef<"section"> & Partial<Props>;

export const TestimonialSection = (props: TestimonialSectionProps) => {
  const { heading, description, testimonials } = {
    ...TestimonialSectionDefaults,
    ...props,
  };

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <section id="depoimentos" className="overflow-hidden  py-10 md:py-24  ">
      <div className="container">
        <div className="rb-12 mb-12 md:mb-18 lg:mb-20 md:max-w-[50%]">
          <h2 className="rb-5 mb-5 text-3xl text-n-primary font-semibold md:mb-6 md:text-4xl lg:text-5xl">
            {heading}
          </h2>
          <p className="md:text-lg text-n-text">{description}</p>
        </div>
        <Carousel
          setApi={setApi}
          opts={{
            loop: true,
            align: "start",
          }}
        >
          <CarouselContent className="ml-0">
            {testimonials.map((testimonial, index) => (
              <CarouselItem
                key={index}
                className="basis-[95%] pl-0 pr-6 pb-5 sm:basis-[80%] md:basis-1/2 md:pr-8 lg:basis-1/3"
              >
                <TestimonialCard testimonial={testimonial} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="rt-8 mt-8 flex items-center justify-between">
            <div className="mt-5 flex w-full items-start justify-start">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={clsx("mx-[3px] inline-block size-2 rounded-full", {
                    "bg-black": current === index + 1,
                    "bg-neutral-300": current !== index + 1,
                  })}
                />
              ))}
            </div>
            <div className="flex items-end justify-end gap-2 md:gap-4">
              <CarouselPrevious className="static right-0 top-0 size-12 -translate-y-0" />
              <CarouselNext className="static right-0 top-0 size-12 -translate-y-0" />
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
};

const EstrelasRating = ({ nota }: { nota: number }) => {
  const estrelas = [];

  for (let i = 1; i <= 5; i++) {
    if (nota >= i) {
      estrelas.push(<FaStar key={i} className="text-n-secondary" />);
    } else if (nota >= i - 0.5) {
      estrelas.push(<FaStarHalfAlt key={i} className="text-n-secondary" />);
    } else {
      estrelas.push(<FaRegStar key={i} className="text-n-secondary" />);
    }
  }

  return <div className="flex gap-1 mb-3">{estrelas}</div>;
};

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = testimonial.quote.length > 300;
  return (
    <div className="flex w-full flex-col items-start justify-center bg-white shadow-lg p-6 md:p-8 rounded-[16px] min-h-[220px]">
      <EstrelasRating nota={testimonial.stars ?? 5} />
      <blockquote
        className={clsx(
          "md:text-md text-n-text-green transition-all duration-300 ease-in-out",
          {
            "line-clamp-5": !expanded && shouldTruncate,
          }
        )}
      >
        {testimonial.quote}
      </blockquote>
      {shouldTruncate && (
        <button
          className="mt-2 text-sm text-n-green-light hover:underline focus:outline-none"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Mostrar menos" : "Mostrar mais"}
        </button>
      )}
      <div className="mt-5 flex w-full flex-col items-start gap-4 md:mt-6 md:w-auto md:flex-row md:items-center">
        <div>
          <img
            src={testimonial.avatar.src}
            alt={testimonial.avatar.alt}
            className="size-12 min-h-12 min-w-12 rounded-full object-cover"
          />
        </div>
        <div>
          <p className="font-semibold">{testimonial.name}</p>
          <p className="text-xs text-n-text-neutral">
            <span>{testimonial.data}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

const testimonial1 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/webflow-logo.svg",
    alt: "Webflow logo 1",
  },
  quote:
    "Contratei a empresa para transportar do Rio de Janeiro pra Florianopolis um carro Toyota Yaris. Fui primeiramente atendida pelo WhatsApp pelo funcionário André Luiz, que muito atenciosamente me deu todas as informações que eu precisava, uma vez que eu nunca tinha feito este tipo de transporte. Em seguida me encaminhou para o funcionário Mikael, responsável pela área financeira, que negociou comigo um preço competitivo com as concorrentes que eu estava pesquisando. O serviço da Nacional GO foi impecável durante todo o processo, paguei o serviço à vista e o carro chegou antes do prazo previsto, intacto, sem um arranhão. Super recomendo a empresa.",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjVLr1X4FkacspPiN2WL2Uf8mN1HzT_K_TxxfKQRv4v3c-Hx8gM=w40-h40-c-rp-mo-br100",
    alt: "Tamara Abdalla",
  },
  name: "Tamara Abdalla",
  position: "Position",
  data: "26-09-2024",
  stars: 5,
};

const testimonial2 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Excelente! Fui atendido por Fernando que desde o início me atendeu muito bem e sempre tirando todas as dúvidas. O carro chegou no período previsto e em perfeitas condições!!! Recomendo.",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjWPpr_YzlIplvYutNvCGcnl-3wIuy32kpPDhxWMb5EY8SZ-X2hR=w72-h72-p-rp-mo-br100",
    alt: "CLEIDIANO SANTOS",
  },
  name: "CLEIDIANO SANTOS",
  position: "Gerente",
  data: "14-10-2024",
  stars: 5,
};

const testimonial3 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Gostei muito do atendimento e da disponibilidade de Mikael e do pessoal da operação. A moto foi e voltou sem nenhum problema. Recomendo!!!",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjXVHM30vP1t9P64AKZpS4mk93d_nRBAYd-sf8RCZzu7oCgtwFIF=w40-h40-c-rp-mo-br100",
    alt: "cesarjoau1",
  },
  name: "cesarjoau1",
  position: "Gerente",
  data: "25-09-2024",
  stars: 5,
};
const testimonial4 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Queria agradecer ao Matheus Rocha pelo ótimo atendimento e apoio que recebi desde o início do embarque até hoje quando retirei o veículo!🙏",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjVf3ZedPC5mkpbgNIVP2dobzK4yWnuO66JTNiwcRyg3UtTqzhwxmg=w72-h72-p-rp-mo-br100",
    alt: "Lucas Félix",
  },
  name: "Lucas Félix",
  position: "Gerente",
  data: "07-06-2025",
  stars: 5,
};
const testimonial5 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Minha experiência com essa transportadora foi muito positiva. Cumpriram fielmente com as datas e horários acordados, além de nos ter atendido, com atenção, sempre que precisávamos de algum esclarecimento. Excelente atendimento.",
  avatar: {
    src: "https://lh3.googleusercontent.com/a/ACg8ocLiwZFb_jXCoM7JoGhHoqXv-4PmkSQBhDMB6mU_g6R2ZBt1pQ=w72-h72-p-rp-mo-br100",
    alt: "Hélio José Santos",
  },
  name: "Hélio José Santos",
  position: "Gerente",
  data: "15-05-2024",
  stars: 5,
};

const testimonial6 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Gostei bastante do serviço. O carro chegou antes da previsão, embora eu tenha pegado no dia previsto. Tudo certo, e as situações que ocorreram foram resolvidas prontamente pela equipe. Indico o serviço da empresa. Trouxe meu carro de Salvador para o Rio de Janeiro. Prazo de entrega de 1 semana foi cumprido.",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjUFzSZYPRwmrJHDEpBiJKHwKU5zIJrrtAsmkhnuc2bloPpyusoXxQ=w72-h72-p-rp-mo-br100",
    alt: "Rafaela Oliveira",
  },
  name: "Rafaela Oliveira",
  position: "Gerente",
  data: "22-11-2024",
  stars: 5,
};

const testimonial7 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Fui atendida pelo Diego, fiz o transporte do meu carro de Cuiabá MT para São José dos Pinhais PR, o transporte foi rápido e fui muito bem atendida sempre que precisei.",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjV4PPREWwh9d0rC8Cc4aibis5YzgL0O9eXCi2URg5q6miKjaIAZ=w72-h72-p-rp-mo-br100",
    alt: "Duda Milhomem",
  },
  name: "Duda Milhomem",
  position: "Gerente",
  data: "03-06-2024",
  stars: 5,
};

const testimonial8 = {
  logo: {
    src: "https://d22po4pjz3o32e.cloudfront.net/relume-logo.svg",
    alt: "Relume logo 2",
  },
  quote:
    "Minha experiência com eles foi excelente tudo foi feito exatamente conforme combinado, entregaram o carro antes do prazo combinado total transparência e tirar todas as minhas dúvidas a todo momento que eu tinha, super recomendo essa empresa",
  avatar: {
    src: "https://lh3.googleusercontent.com/a-/ALV-UjUajxsP_0uDplIj-al0A2iVKcfRtiLmommcnsb1qc03ChlwnLpe=w72-h72-p-rp-mo-ba2-br100",
    alt: "Blink Men",
  },
  name: "Blink Men",
  position: "Gerente",
  data: "03-06-2024",
  stars: 5,
};

export const TestimonialSectionDefaults: Props = {
  heading: "O que os clientes satisfeitos falam sobre nós?",
  description:
    "Confira o que alguns de nossos clientes dizem sobre a Nacional GO:",
  testimonials: [
    testimonial1,
    testimonial2,
    testimonial3,
    testimonial4,
    testimonial5,
    testimonial6,
    testimonial7,
    testimonial8,
  ],
};
